import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  Fab,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Category as CategoryIcon,
  Label,
  Search,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { categoryService } from '../../services/categoryService'
import { tagService } from '../../services/tagService'
import { Category, Tag } from '../../types'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ConfirmDialog from '../../components/Common/ConfirmDialog'
import { useAuth } from '../../contexts/AuthContext'

const categorySchema = yup.object({
  name: yup.string().required('Category name is required').min(2, 'Name must be at least 2 characters'),
  description: yup.string(),
  color: yup.string().matches(/^#[0-9A-F]{6}$/i, 'Invalid color format'),
})

const tagSchema = yup.object({
  name: yup.string().required('Tag name is required').min(2, 'Name must be at least 2 characters'),
  color: yup.string().matches(/^#[0-9A-F]{6}$/i, 'Invalid color format'),
})

type CategoryFormData = yup.InferType<typeof categorySchema>
type TagFormData = yup.InferType<typeof tagSchema>

const CategoriesPage: React.FC = () => {
  const { user } = useAuth()
  const [categories, setCategories] = useState<Category[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<'categories' | 'tags'>('categories')
  
  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [tagDialogOpen, setTagDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ type: 'category' | 'tag', id: number, name: string } | null>(null)
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedItem, setSelectedItem] = useState<{ type: 'category' | 'tag', item: Category | Tag } | null>(null)

  // Forms
  const categoryForm = useForm<CategoryFormData>({
    resolver: yupResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      color: '#1976d2',
    }
  })

  const tagForm = useForm<TagFormData>({
    resolver: yupResolver(tagSchema),
    defaultValues: {
      name: '',
      color: '#1976d2',
    }
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [categoriesData, tagsData] = await Promise.all([
        categoryService.getCategories(),
        tagService.getTags()
      ])
      setCategories(categoriesData)
      setTags(tagsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (user && localStorage.getItem('auth_token')) {
      console.log('CategoriesPage: User authenticated, fetching data')
      fetchData()
    } else {
      console.log('CategoriesPage: User not authenticated, skipping fetch')
    }
  }, [user])

  const handleCategorySubmit = async (data: CategoryFormData) => {
    try {
      if (editingCategory) {
        const updated = await categoryService.updateCategory(editingCategory.id, data)
        setCategories(prev => prev.map(cat => cat.id === editingCategory.id ? updated : cat))
      } else {
        const newCategory = await categoryService.createCategory(data)
        setCategories(prev => [...prev, newCategory])
      }
      setCategoryDialogOpen(false)
      setEditingCategory(null)
      categoryForm.reset()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category')
    }
  }

  const handleTagSubmit = async (data: TagFormData) => {
    try {
      if (editingTag) {
        const updated = await tagService.updateTag(editingTag.id, data)
        setTags(prev => prev.map(tag => tag.id === editingTag.id ? updated : tag))
      } else {
        const newTag = await tagService.createTag(data)
        setTags(prev => [...prev, newTag])
      }
      setTagDialogOpen(false)
      setEditingTag(null)
      tagForm.reset()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tag')
    }
  }

  const handleDelete = async () => {
    if (!itemToDelete) return
    
    try {
      if (itemToDelete.type === 'category') {
        await categoryService.deleteCategory(itemToDelete.id)
        setCategories(prev => prev.filter(cat => cat.id !== itemToDelete.id))
      } else {
        await tagService.deleteTag(itemToDelete.id)
        setTags(prev => prev.filter(tag => tag.id !== itemToDelete.id))
      }
      setDeleteConfirmOpen(false)
      setItemToDelete(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item')
    }
  }

  const openEditDialog = (type: 'category' | 'tag', item: Category | Tag) => {
    if (type === 'category') {
      setEditingCategory(item as Category)
      categoryForm.reset({
        name: item.name,
        description: (item as Category).description || '',
        color: item.color || '#1976d2',
      })
      setCategoryDialogOpen(true)
    } else {
      setEditingTag(item as Tag)
      tagForm.reset({
        name: item.name,
        color: item.color || '#1976d2',
      })
      setTagDialogOpen(true)
    }
    setAnchorEl(null)
  }

  const openDeleteConfirm = (type: 'category' | 'tag', item: Category | Tag) => {
    setItemToDelete({ type, id: item.id, name: item.name })
    setDeleteConfirmOpen(true)
    setAnchorEl(null)
  }

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <Container maxWidth="lg">
        <LoadingSpinner />
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Categories & Tags
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Organize your transactions with custom categories and tags
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
          <Button
            variant={activeTab === 'categories' ? 'contained' : 'text'}
            onClick={() => setActiveTab('categories')}
            startIcon={<CategoryIcon />}
            sx={{ m: 1 }}
          >
            Categories ({categories.length})
          </Button>
          <Button
            variant={activeTab === 'tags' ? 'contained' : 'text'}
            onClick={() => setActiveTab('tags')}
            startIcon={<Label />}
            sx={{ m: 1 }}
          >
            Tags ({tags.length})
          </Button>
        </Box>
      </Paper>

      {/* Search and Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder={`Search ${activeTab}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ flexGrow: 1, minWidth: 200 }}
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            if (activeTab === 'categories') {
              setCategoryDialogOpen(true)
            } else {
              setTagDialogOpen(true)
            }
          }}
        >
          Add {activeTab === 'categories' ? 'Category' : 'Tag'}
        </Button>
      </Box>

      {/* Content */}
      {activeTab === 'categories' ? (
        <Grid container spacing={3}>
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: category.color || '#1976d2'
                          }}
                        />
                        <Typography variant="h6" noWrap>
                          {category.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget)
                          setSelectedItem({ type: 'category', item: category })
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                    {category.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {category.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {category.transactionCount || 0} transactions
                      </Typography>
                      <Chip
                        label={category.isDefault ? 'Default' : 'Custom'}
                        size="small"
                        variant="outlined"
                        color={category.isDefault ? 'primary' : 'default'}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {searchTerm ? 'No categories found' : 'No categories yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {searchTerm 
                    ? 'Try adjusting your search terms'
                    : 'Create your first category to organize transactions'
                  }
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setCategoryDialogOpen(true)}
                  >
                    Create Category
                  </Button>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {filteredTags.length > 0 ? (
            filteredTags.map((tag) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tag.id}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: tag.color || '#1976d2',
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="body1" noWrap>
                          {tag.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget)
                          setSelectedItem({ type: 'tag', item: tag })
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {tag.usageCount || 0} uses
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Label sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {searchTerm ? 'No tags found' : 'No tags yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {searchTerm 
                    ? 'Try adjusting your search terms'
                    : 'Create tags to add more context to your transactions'
                  }
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setTagDialogOpen(true)}
                  >
                    Create Tag
                  </Button>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          display: { xs: 'flex', sm: 'none' }
        }}
        onClick={() => {
          if (activeTab === 'categories') {
            setCategoryDialogOpen(true)
          } else {
            setTagDialogOpen(true)
          }
        }}
      >
        <Add />
      </Fab>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => selectedItem && openEditDialog(selectedItem.type, selectedItem.item)}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => selectedItem && openDeleteConfirm(selectedItem.type, selectedItem.item)}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={categoryForm.handleSubmit(handleCategorySubmit)}>
          <DialogTitle>
            {editingCategory ? 'Edit Category' : 'Create Category'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Controller
                name="name"
                control={categoryForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Category Name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="description"
                control={categoryForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description (Optional)"
                    multiline
                    rows={2}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="color"
                control={categoryForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Color"
                    type="color"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog open={tagDialogOpen} onClose={() => setTagDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={tagForm.handleSubmit(handleTagSubmit)}>
          <DialogTitle>
            {editingTag ? 'Edit Tag' : 'Create Tag'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Controller
                name="name"
                control={tagForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Tag Name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="color"
                control={tagForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Color"
                    type="color"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTagDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingTag ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={`Delete ${itemToDelete?.type}`}
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        severity="error"
      />
    </Container>
  )
}

export default CategoriesPage